import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Select, SelectOption, Text, Avatar} from '@gravity-ui/uikit';
import {
  ActionModal,
  ErrorModal,
  FormInput,
  useErrorHandler,
  useDebounce,
} from '../../../../shared';
import {useBenefactors, useBenefactorLookup} from '../../../../entities';
import {useBenefactorTransferForm} from '../../model';
import {useTransferAmountInput} from '../../lib/useTransferAmountInput';
import {BenefactorTransferConfirmModal} from '../BenefactorTransferConfirmModal';
import styles from './BenefactorTransferModal.module.scss';

/** Размер страницы списка доноров: остальные подгружаются по мере прокрутки. */
const BENEFACTORS_PAGE_SIZE = 50;

/** Задержка перед отправкой поискового запроса на сервер, мс. */
const SEARCH_DEBOUNCE_DELAY = 500;

/**
 * Минимальная длина поискового запроса. По одной-двум буквам осмысленной выдачи не получится,
 * а показывать вместо неё первую страницу без фильтра — только сбивать с толку: набравший «Ян»
 * увидел бы список непричастных имён. Вместо этого показываем подсказку и не ходим на сервер.
 */
const MIN_SEARCH_LENGTH = 3;

/** Серверный поиск идёт по имени, телефону и почте, поэтому идентификатор ищем отдельным запросом. */
const BENEFACTOR_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface BenefactorOptionSource {
  id: string;
  displayName: string;
  realName?: string | null;
  avatarUrl: string | null;
}

type SelectedBenefactor = BenefactorOptionSource;

const toOption = ({id, displayName, realName, avatarUrl}: BenefactorOptionSource): SelectOption => {
  const name = displayName || realName || id;

  return {
    value: id,
    content: name,
    data: {avatarUrl, displayName: name, id},
  };
};

interface BenefactorTransferModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  fundraiserId: string;
}

export const BenefactorTransferModal = ({open, setOpen, fundraiserId}: BenefactorTransferModalProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedBenefactor, setSelectedBenefactor] = useState<SelectedBenefactor | null>(null);

  const debouncedQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_DELAY).trim();
  const searchedBenefactorId = BENEFACTOR_ID_PATTERN.test(debouncedQuery) ? debouncedQuery : '';

  // Про слишком короткий запрос судим по набранному сейчас, а не по отложенному: подсказка
  // должна появляться сразу, не дожидаясь окончания задержки.
  const currentQueryLength = searchQuery.trim().length;
  const isSearchTooShort = currentQueryLength > 0 && currentQueryLength < MIN_SEARCH_LENGTH;

  const {getInfiniteBenefactors, updateFilter} = useBenefactors({pageSize: BENEFACTORS_PAGE_SIZE, query: ''});
  const {data: benefactorById} = useBenefactorLookup(searchedBenefactorId);
  const {
    data: benefactorsData,
    fetchNextPage,
    hasNextPage,
    isFetching: isFetchingBenefactors,
  } = getInfiniteBenefactors;

  const {handleError, isErrorModalOpen, errorInfo, handleClose} = useErrorHandler({
    resourceName: 'Трансфер средств',
    title: 'Ошибка при внесении средств',
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isLoading,
    submitError,
    resetForm,
    submitForm,
    setFieldValue,
    setFieldTouched,
    showConfirmModal,
    setShowConfirmModal,
    formDataForConfirm,
    handleConfirmTransfer,
  } = useBenefactorTransferForm({
    fundraiserId,
    setOpen,
    onSuccess: () => {
      setOpen(false);
    },
  });

  // Поиск выполняется на сервере: пользователей десятки тысяч, и локально отфильтровать
  // можно было бы только уже загруженную страницу.
  useEffect(() => {
    updateFilter({query: debouncedQuery.length < MIN_SEARCH_LENGTH ? '' : debouncedQuery});
  }, [debouncedQuery, updateFilter]);

  // Выбранный донор всегда остаётся в списке опций: после смены поискового запроса выборка
  // перезагружается, и без этого Select потерял бы отображение выбранного значения.
  const benefactorOptions = useMemo<SelectOption[]>(() => {
    // на слишком короткий запрос показываем подсказку, а не выборку без фильтра
    if (isSearchTooShort) {
      return [];
    }

    const options: SelectOption[] = benefactorsData?.pages.flatMap((page) => page.items.map(toOption)) ?? [];

    const pinBenefactor = (benefactor?: BenefactorOptionSource | null) => {
      if (benefactor && !options.some((option) => option.value === benefactor.id)) {
        options.unshift(toOption(benefactor));
      }
    };

    pinBenefactor(benefactorById);
    pinBenefactor(selectedBenefactor);

    return options;
  }, [benefactorsData, benefactorById, selectedBenefactor, isSearchTooShort]);

  // Обработка ошибок
  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  // Очистка формы при открытии модального окна
  useEffect(() => {
    if (open) {
      resetForm();
      setSearchQuery('');
      setSelectedBenefactor(null);
    }
  }, [open, resetForm]);

  const handleBenefactorChange = (newValue: string[]) => {
    const benefactorId = newValue[0] || '';
    const option = benefactorOptions.find((opt) => opt.value === benefactorId);

    setFieldValue('benefactorId', benefactorId);
    setSelectedBenefactor(
      benefactorId
        ? {
            id: benefactorId,
            displayName: (option?.content as string) || benefactorId,
            avatarUrl: option?.data?.avatarUrl ?? null,
          }
        : null,
    );
  };

  const handleBenefactorBlur = () => {
    setFieldTouched('benefactorId', true);
  };

  // Индикатор подгрузки остаётся на экране и во время обычного перезапроса выборки, поэтому
  // ждём завершения любого запроса, а не только подгрузки страницы: у бесконечного запроса одна
  // запись в кэше, и параллельный fetchNextPage поверх refetch отменил бы её. Когда запрос
  // завершится, Select пересоздаст observer и вызовет подгрузку заново.
  const handleLoadMoreBenefactors = useCallback(() => {
    if (hasNextPage && !isFetchingBenefactors) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingBenefactors, fetchNextPage]);

  const amountInput = useTransferAmountInput(values.amount, (amount) => {
    setFieldValue('amount', amount);
    setFieldTouched('amount', true, false);
  });

  const serviceFeeInput = useTransferAmountInput(values.serviceFee, (serviceFee) => {
    setFieldValue('serviceFee', serviceFee);
    setFieldTouched('serviceFee', true, false);
  });

  // Кастомное отображение опций в выпадающем списке
  const renderOption = (option: SelectOption) => (
    <div className={styles.option}>
      <Avatar imgUrl={option.data?.avatarUrl || ''} text={option.data?.displayName || ''} size="s" />
      <Text variant="body-1">
        {option.content}{' '}
        <Text variant="body-1" color="secondary">
          ID:{option.data?.id}
        </Text>
      </Text>
    </div>
  );

  // Кастомное отображение выбранного значения (только текст без аватарки)
  const renderSelectedOption = (option: SelectOption) => <span>{option.content}</span>;

  // Отбор выполняет сервер, поэтому локальную фильтрацию Select отключаем: иначе результат
  // поиска был бы ещё раз отсеян по загруженной странице.
  const acceptAllOptions = () => true;

  const renderEmptyOptions = ({filter}: {filter: string}) => {
    const length = filter.trim().length;
    let message = 'Список пуст';

    if (length > 0 && length < MIN_SEARCH_LENGTH) {
      message = `Введите не менее ${MIN_SEARCH_LENGTH} символов`;
    } else if (length > 0) {
      message = 'Никого не нашли';
    }

    return (
      <Text variant="body-1" color="secondary">
        {message}
      </Text>
    );
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Внести средства пользователя'}
        textButtonApply={'Проверить данные'}
        textButtonCancel={'Отменить'}>
        <div className={styles.field}>
          <Text variant="subheader-1" style={{marginBottom: '4px', display: 'block'}}>
            Донор сбора<span style={{color: 'red'}}>*</span>
          </Text>
          <Select
            size="xl"
            className={styles.select}
            placeholder="Выберите донора"
            value={values.benefactorId ? [values.benefactorId] : []}
            onUpdate={handleBenefactorChange}
            onBlur={handleBenefactorBlur}
            options={benefactorOptions}
            renderOption={renderOption}
            renderSelectedOption={renderSelectedOption}
            renderEmptyOptions={renderEmptyOptions}
            filterable
            filterOption={acceptAllOptions}
            filterPlaceholder="Имя, телефон, почта или ID"
            onFilterChange={setSearchQuery}
            loading={!isSearchTooShort && (isFetchingBenefactors || hasNextPage)}
            onLoadMore={handleLoadMoreBenefactors}
            hasClear
            errorMessage={touched.benefactorId && errors.benefactorId}
            validationState={
              touched.benefactorId && errors.benefactorId && !values.benefactorId ? 'invalid' : undefined
            }
          />
        </div>

        <FormInput
          label="Сумма доната"
          placeholder="Сумма доната"
          name="amount"
          type="text"
          {...amountInput}
          onBlur={handleBlur}
          error={touched.amount && errors.amount}
          isRequired
        />

        <FormInput
          label="Комиссия проекта"
          placeholder="Комиссия проекта"
          name="serviceFee"
          type="text"
          {...serviceFeeInput}
          onBlur={handleBlur}
          error={touched.serviceFee && errors.serviceFee}
          isRequired
        />

        <FormInput
          label="Номер договора"
          placeholder="Номер договора"
          name="bookkeepingNumber"
          value={values.bookkeepingNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.bookkeepingNumber && errors.bookkeepingNumber}
        />

        <FormInput
          label="Комментарий для бухгалтера"
          name="note"
          isTextarea
          placeholder="Комментарий"
          value={values.note}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.note && errors.note}
        />
      </ActionModal>

      <BenefactorTransferConfirmModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        formData={formDataForConfirm}
        selectedBenefactor={selectedBenefactor}
        onConfirm={handleConfirmTransfer}
        isLoading={isLoading}
      />

      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
